<template>
  <SettingsPagePanel>
    <SettingsPagePanelHeader :showSubtitle="true">
      <template #title>日程調整カレンダー 新規作成</template>
      <template #right>
        <div>
          <FlatButton
            :class="$style.button"
            variant="secondary"
            @click="handleCancel"
          >
            キャンセル
          </FlatButton>
          <FlatButton
            :class="$style.button"
            variant="primary"
            :disabled="isSubmitDisabled"
            @click="handleSubmit"
          >
            保存
          </FlatButton>
        </div>
      </template>
    </SettingsPagePanelHeader>
    <CalendarForm v-model="form" :canEdit="canEditCalendarSetting" />
    <div :class="$style.buttonsInBottom">
      <FlatButton
        :class="$style.button"
        variant="secondary"
        @click="handleCancel"
      >
        キャンセル
      </FlatButton>
      <FlatButton
        :class="$style.button"
        variant="primary"
        :disabled="isSubmitDisabled"
        @click="handleSubmit"
      >
        この内容で作成する
      </FlatButton>
    </div>
  </SettingsPagePanel>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import { cloneDeep, isEqual } from 'lodash-es'

import FlatButton from '@/components/atoms/FlatButton.vue'
import type { CreateCalendarInput } from '@/types/schema.generated.ts'
import { useAlert } from '@/libs/alertPortal'
import { useLogger } from '@/logger/logger'
import { captureException as newrelicCaptureException } from '@/newrelic'

import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import SettingsPagePanel from '../SettingsPagePanel.vue'

import { weekdayNames } from './calendar.ts'
import {
  type FormType,
  validateForm,
  mapFormToCalendarSettingInput,
} from './formSchema.ts'
import CalendarForm from './CalendarForm.vue'
import { useCreateCalendarMutation } from './SettingsPageCalendarCreateView.generated.ts'
import { useCalendarSettingsPageQuery } from './SettingsPageCalendarView.generated.ts'

const emit = defineEmits<{
  (
    event: 'onBeforeLeaveConfirmation',
    callbacks: {
      onConfirm: () => void
      onAbort: () => void
    }
  ): void
}>()

const alert = useAlert()
const logger = useLogger()
const router = useRouter()
const route = useRoute('calendarSettingCreate')

const initialForm: FormType = {
  publicTitle: '',
  privateTitle: '',
  description: '',
  status: 'ACTIVE',
  durationMinutes: 60,
  offsetMinutes: 60,
  slotGenerationDelayHours: 18,
  slotGenerationPeriodDays: 14,
  assignedStaffs: [],
  staffSelectionStrategy: 'RANDOM', // Default strategy
  dailyAvailabilities: weekdayNames
    .keys()
    .map(dayOfWeek => ({
      dayOfWeek,
      from: { hours: 9, minutes: 0 },
      until: { hours: 22, minutes: 0 },
    }))
    .toArray(),
  questions: [],
}

const form = ref<FormType>(cloneDeep(initialForm))

const { result: calendarSettingsPageQueryResult } =
  useCalendarSettingsPageQuery()
const canEditCalendarSetting = computed(
  () =>
    calendarSettingsPageQueryResult.value?.uiControl
      .editOkanekoCalendarSetting ?? false
)

const isSubmitting = ref(false)
const isSubmitDisabled = computed(() => {
  const errors = validateForm(form.value)
  return isSubmitting.value || errors !== undefined
})

const { mutate } = useCreateCalendarMutation()

const handleSubmit = async () => {
  if (isEqual(initialForm, form.value)) {
    return
  }

  isSubmitting.value = true

  const input: CreateCalendarInput = {
    setting: mapFormToCalendarSettingInput(form.value),
  }

  try {
    logger.ltlPeople.event.createCalendarSetting(logger.buildMeta(), {
      data: {
        status: form.value.status,
        privateTitle:
          form.value.privateTitle !== '' ? form.value.privateTitle : undefined,
        publicTitle: form.value.publicTitle,
        description: form.value.description
          ? form.value.description
          : undefined,
        slotDurationMinutes: form.value.durationMinutes,
        slotOffsetMinutes: form.value.offsetMinutes,
        slotGenerationDelayHours: form.value.slotGenerationDelayHours,
        slotGenerationPeriodDays: form.value.slotGenerationPeriodDays,
        dailyAvailabilities: form.value.dailyAvailabilities,
        assignedStaffs: form.value.assignedStaffs,
        staffSelectionStrategy: form.value.staffSelectionStrategy,
        questions: form.value.questions,
      },
    })

    const payload = await mutate({ input })

    if (payload?.errors || !payload?.data) {
      throw new Error('create calendar failed')
    }

    // eslint-disable-next-line fp/no-mutating-methods
    router.push({
      name: 'calendarSettingDetail',
      params: {
        entranceGUID: route.params.entranceGUID,
        calendarGUID: payload.data.createCalendar.guid,
      },
    })
    alert.success('日程調整カレンダーを作成しました。')
  } catch (error) {
    newrelicCaptureException(error)
    alert.error('エラーが発生しました。')
    isSubmitting.value = false
  }
}

const handleCancel = async () => {
  // eslint-disable-next-line fp/no-mutating-methods
  await router.push({
    name: 'calendarSetting',
    params: {
      entranceGUID: route.params.entranceGUID,
    },
  })
}

onBeforeRouteLeave((to, from, next) => {
  if (isEqual(initialForm, form.value) || isSubmitting.value) {
    next()
    return
  }
  emit('onBeforeLeaveConfirmation', {
    onConfirm: () => {
      next()
    },
    onAbort: () => {},
  })
})
</script>

<style lang="scss" module>
.button {
  &:nth-child(n + 2) {
    margin-left: 16px;
  }
}

.buttonsInBottom {
  display: flex;
  justify-content: center;
  margin-top: 24px;
}
</style>
